import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  TablePagination,
  TableSortLabel,
  Avatar,
  alpha,
  Stack,
  useTheme,
  CircularProgress,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Person,
  Business,
  LocationOn,
  Close as CloseIcon,
  PriorityHigh,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';


import { Client } from '../../types';
import { formatarCPFCNPJ, formatarData, formatarValor } from '../../utils/formatToView';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type OrdemCampo = 'id' | 'nome' | 'createdAt';
type OrdemDirecao = 'asc' | 'desc';

export interface ClientesTableProps {
  /** Lista de clientes a exibir */
  clientes: Client[];
  /** Total de registros (para paginação) */
  totalRegistros?: number;
  /** Estado de loading */
  loading?: boolean;
  /** Erro ao buscar dados */
  error?: boolean;
  /** Se true, a tabela possui paginação e sorting controlados externamente */
  serverSide?: boolean;
  /** Paginação controlada */
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /** Ordenação controlada */
  orderBy?: OrdemCampo;
  order?: OrdemDirecao;
  onSort?: (campo: OrdemCampo) => void;
  /** Título opcional acima da tabela */
  titulo?: string;
  subtitulo?: string;
  /** Se true, mostra a tabela dentro de um Collapse com botão fechar */
  dismissible?: boolean;
  onClose?: () => void;
  open?: boolean;
  /** Cor do tema para a barra do header */
  headerColor?: string;
  /** Altura máxima da tabela */
  maxHeight?: number;
  /** Cor customizada para o texto do header */
  headerTextColor?: string;
  /** Se true, mostra colunas extras de inadimplência (faturas/vencimento) */
  showDelinquencyInfo?: boolean;
  /** Indica se algo está sendo buscado de novo */
  isFetching?: boolean;
  /** Evento de clique na linha */
  onRowClick?: (client: Client) => void;
  /** Habilitar checkbox de seleção */
  selectable?: boolean;
  /** IDs dos clientes selecionados */
  selectedIds?: Set<number>;
  /** Callback ao selecionar/deselecionar todos */
  onSelectAll?: (checked: boolean) => void;
  /** Callback ao selecionar/deselecionar uma linha */
  onSelectRow?: (id: number, checked: boolean) => void;
}


// ─── Chip de status ────────────────────────────────────────────────────────────
const statusConfig: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }
> = {
  ativo: { label: 'Ativo', color: 'success' },
  bloqueado: { label: 'Bloqueado', color: 'warning' },
  inativo: { label: 'Inativo', color: 'default' },
  suspenso: { label: 'Suspenso', color: 'error' },
  inadimplente: { label: 'Inadimplente', color: 'error' },
};

// ─── Componente ───────────────────────────────────────────────────────────────

const ClientesTable: React.FC<ClientesTableProps> = ({
  clientes,
  totalRegistros,
  loading = false,
  error = false,
  serverSide = false,
  page = 0,
  rowsPerPage = 30,
  onPageChange,
  onRowsPerPageChange,
  orderBy = 'createdAt',
  order = 'desc',
  onSort,
  titulo,
  subtitulo,
  dismissible = false,
  onClose,
  open = true,
  headerColor,
  headerTextColor,
  showDelinquencyInfo = false,
  maxHeight,
  isFetching = false,
  onRowClick,
  selectable = false,
  selectedIds = new Set(),
  onSelectAll,
  onSelectRow,
}) => {

  const navigate = useNavigate();
  const theme = useTheme();

  const total = totalRegistros ?? clientes.length;
  const corHeader = headerColor || theme.palette.primary.main;

  // Status real (considera contratos bloqueados)
  const getStatusReal = (client: Client): string => {
    if ((client.contratos ?? []).some((c) => c.status === 'bloqueado')) return 'bloqueado';
    return client.status;
  };

  // Paginação / sort local quando não é server-side
  const displayClientes = React.useMemo(() => {
    if (serverSide) return clientes;
    let list = [...clientes];
    // sort
    list.sort((a: any, b: any) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (av === undefined || av === null) av = '';
      if (bv === undefined || bv === null) bv = '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return order === 'asc' ? av - bv : bv - av;
      }
      const sa = String(av).toLowerCase();
      const sb = String(bv).toLowerCase();
      if (sa < sb) return order === 'asc' ? -1 : 1;
      if (sa > sb) return order === 'asc' ? 1 : -1;
      return 0;
    });
    // paginate
    return list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [clientes, serverSide, orderBy, order, page, rowsPerPage]);

  const handleSort = (campo: OrdemCampo) => {
    if (onSort) onSort(campo);
  };

  const tableContent = (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header com título (quando dismissible) */}
      {(titulo || dismissible) && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: headerColor ? corHeader : alpha(corHeader, 0.06),
              color: headerTextColor || (headerColor ? 'white' : 'text.primary'),
              borderBottom: headerColor ? 'none' : `1px solid ${alpha(corHeader, 0.1)}`
            }}
          >
            <Box>
              {titulo && (
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'inherit'
                  }}
                >
                  {titulo}
                </Typography>
              )}
              {subtitulo && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    color: headerTextColor ? alpha(headerTextColor, 0.8) : (headerColor ? alpha('#fff', 0.8) : 'text.secondary')
                  }}
                >
                  {subtitulo}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(isFetching || loading) && <CircularProgress size={16} thickness={4} />}
              {dismissible && onClose && (
                <IconButton size="small" onClick={onClose} sx={{ bgcolor: alpha(theme.palette.error.main, 0.08) }}>
                  <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                </IconButton>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      <TableContainer sx={{ overflowX: 'auto', maxHeight: maxHeight ? maxHeight : 'none' }}>
        <Table size="small" stickyHeader={!!maxHeight} sx={{ minWidth: 700, '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${corHeader} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(corHeader, 0.2)}`,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selectedIds.size > 0 && selectedIds.size < clientes.length}
                    checked={clientes.length > 0 && selectedIds.size === clientes.length}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sx={{ width: 70 }}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'nome'}
                  direction={orderBy === 'nome' ? order : 'asc'}
                  onClick={() => handleSort('nome')}
                >
                  Cliente
                </TableSortLabel>
              </TableCell>
              <TableCell>CPF / CNPJ</TableCell>
              <TableCell>Localização</TableCell>
              <TableCell sx={{ width: 110 }}>Status</TableCell>

              {showDelinquencyInfo && (
                <>
                  <TableCell align="center" sx={{ width: 80, fontWeight: 800 }}>Faturas</TableCell>
                  <TableCell align="center" sx={{ width: 120, fontWeight: 800 }}>Vencimento</TableCell>
                  <TableCell align="right" sx={{ width: 120, fontWeight: 800 }}>Valor Total</TableCell>
                </>
              )}

              {!showDelinquencyInfo && (
                <TableCell sx={{ width: 130 }}>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'desc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    Dt. Cadastro
                  </TableSortLabel>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: showDelinquencyInfo ? 8 : 6 }).map((_, j) => (
                    <TableCell key={j} sx={{ py: 0.8 }}>
                      <Box
                        sx={{
                          height: 14,
                          borderRadius: 0.5,
                          bgcolor: alpha(theme.palette.text.disabled, 0.12),
                          width: j === 1 ? '80%' : j === 0 ? '40%' : '60%',
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={showDelinquencyInfo ? 8 : 6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="error">
                    Erro ao carregar clientes. Tente novamente.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : displayClientes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showDelinquencyInfo ? 8 : 6} align="center" sx={{ py: 5 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.text.disabled, 0.1),
                        width: 48,
                        height: 48,
                      }}
                    >
                      <Person sx={{ color: 'text.disabled', fontSize: 28 }} />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Nenhum cliente encontrado
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              displayClientes.map((client) => {
                const statusReal = getStatusReal(client);
                const cfg = statusConfig[statusReal] ?? { label: statusReal, color: 'default' };
                return (
                  <TableRow
                    key={client.id}
                    hover
                    onClick={() => {
                      if (onRowClick) {
                        onRowClick(client);
                      } else {
                        navigate(`/clients/${client.id}`);
                      }
                    }}
                    sx={{

                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(client.id)}
                          onChange={(e) => onSelectRow?.(client.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                      >
                        #{client.id}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Avatar
                          sx={{
                            width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: 'primary.main', flexShrink: 0,
                          }}
                        >
                          {client.tipo_pessoa === 'pj' ? (
                            <Business sx={{ fontSize: 14 }} />
                          ) : (
                            client.nome?.charAt(0)?.toUpperCase() ?? 'C'
                          )}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                              {client.nome}
                            </Typography>
                            {client.cobranca_id && (
                              <Tooltip title="Já existe uma cobrança ativa para este cliente" arrow placement="top">
                                <PriorityHigh
                                  sx={{
                                    fontSize: 20,
                                    color: 'warning.main',
                                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                                    borderRadius: '50%',
                                    p: 0.1
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1 }}>
                            {client.tipo_pessoa === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                          </Typography>
                        </Box>

                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {formatarCPFCNPJ(client.cpf_cnpj)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3 }}>
                        <LocationOn sx={{ fontSize: 13, color: 'text.secondary', mt: 0.2, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                            {[client.endereco, client.numero].filter(Boolean).join(', ')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1 }}>
                            {[client.bairro, client.cidade, client.estado].filter(Boolean).join(' - ')}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 0.6, px: 1.5 }}>
                      <Chip
                        label={cfg.label}
                        color={cfg.color}
                        size="small"
                        sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                      />
                    </TableCell>

                    {showDelinquencyInfo && (
                      <>
                        <TableCell align="center">
                          <Chip
                            label={(client as any)._qtyVencidas || 0}
                            size="small"
                            sx={{ fontWeight: 800, fontSize: '0.75rem', height: 22, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}
                          />
                          {client.cobranca_id && (
                            <Tooltip title="Já existe uma cobrança ativa para este cliente" arrow placement="top">
                              <PriorityHigh
                                sx={{
                                  fontSize: 20,
                                  color: 'warning.main',
                                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                                  borderRadius: '50%',
                                  p: 0.1
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'error.main' }}>
                            {(client as any)._oldestVencimento ? formatarData((client as any)._oldestVencimento) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 800, color: 'error.main' }}>
                            {formatarValor((client as any)._totalDevido || 0)}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    {!showDelinquencyInfo && (
                      <TableCell sx={{ py: 0.6, px: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {client.createdAt ? formatarData(client.createdAt) : '—'}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.8,
          px: 2,
          bgcolor: alpha(corHeader, 0.04),
          borderTop: `1px solid ${alpha(corHeader, 0.15)}`,
          position: 'relative',
          flexWrap: 'nowrap',
          gap: 1,
        }}
      >
        <Box sx={{ position: { xs: 'static', md: 'absolute' }, left: 20, display: { xs: 'none', md: 'block' } }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {isFetching && !loading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <CircularProgress size={11} thickness={5} />
                Atualizando...
              </span>
            ) : (
              <>
                Total: <strong>{total.toLocaleString('pt-BR')}</strong> cliente{total !== 1 ? 's' : ''}
              </>
            )}
          </Typography>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
          onPageChange={(_e, p) => onPageChange?.(p)}
          onRowsPerPageChange={(e) => onRowsPerPageChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por pág:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count.toLocaleString('pt-BR') : `+${to}`}`
          }
          sx={{
            width: '100%',
            '.MuiTablePagination-toolbar': {
              minHeight: 32,
              flexWrap: 'nowrap',
              justifyContent: 'center',
              gap: 1,
              p: 0,
              '& .MuiTablePagination-spacer': { display: 'none' },
            },
            '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
            '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
            '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
            '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
          }}
        />
      </Box>
    </Paper>
  );

  // Se dismissible, envolve em Collapse
  if (dismissible) {
    return (
      <Collapse in={open} timeout={300} unmountOnExit>
        <Box sx={{ mt: 1.5, mb: 3 }}>
          {tableContent}
        </Box>
      </Collapse>
    );
  }

  return tableContent;
};

export default ClientesTable;
